<template>
  <div>
    <!-- Description Text -->
    <BaseText type="body" size="sm" class="px-8 my-4 text-text-normal">
      Use this function to upload your own content to Foreplay for use in Boards and Briefs.
    </BaseText>

    <!-- Upload Area -->
    <label
      for="manualUploadInput"
      class="block m-4 cursor-pointer"
      @dragenter.prevent="dragActive = true"
      @dragover.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="onDrop"
    >
      <div
        class="flex flex-col items-center justify-center gap-2 h-48 border-2 border-dashed rounded-xl bg-gradient-to-b to-transparent transition duration-300"
        :class="dragActive ? 'border-primary-blue-100 from-primary-blue-25' : 'border-primary-blue-50 from-primary-blue-10 hover:opacity-70'"
      >
        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-primary-blue-10 text-primary-blue-100">
          <UploadIcon />
        </div>
        <BaseText type="label" size="md" class="text-text-muted">
          Drag and drop or click to upload
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-subdued">
          .mp4, .png or .jpeg - up to 500MB each
        </BaseText>
      </div>
    </label>
    <input
      id="manualUploadInput"
      ref="fileInput"
      type="file"
      hidden
      multiple
      :accept="acceptedTypes"
      @change="onInputChange"
    >

    <!-- Uploaded Files -->
    <div v-if="uploads.length" class="grid grid-cols-2 px-5 pt-2 pb-2">
      <BaseText type="label" size="sm" class="text-text-normal">
        Asset
      </BaseText>
      <BaseText type="label" size="sm" class="text-text-normal">
        Add to Board
      </BaseText>
    </div>
    <div class="w-full pr-1.5">
      <div
        v-if="uploads.length"
        ref="uploaded-files-list"
        class="uploaded-files-list flex flex-col gap-2 w-full overflow-y-scroll mb-2"
      >
        <div v-for="(upload, index) in uploads" :key="upload.key" :style="{ zIndex: uploads.length - index }">
          <File
            :upload="upload"
            :dropdownOpenedId.sync="dropdownOpenedId"
            @retry="retryUpload(upload)"
            @updateBoards="$emit('updateFileBoards', upload.ad.id, $event)"
          />
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 mt-4 mr-5">
      <BaseText v-if="isBusy" type="body" size="sm" class="text-text-subdued">
        Uploading {{ inProgressCount }} {{ inProgressCount === 1 ? 'file' : 'files' }}...
      </BaseText>
      <BaseButton primary @click="$emit('close')">
        Done
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid'
import { mapGetters, mapMutations } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import firebase from '@/api/config/FirebaseConfig'

import File from './File.vue'
import UploadIcon from '../../globals/Icons/UploadIcon.vue'

const MANUAL_UPLOAD_BUCKET = 'gs://foreplay-manual-upload'
const MAX_FILE_BYTES = 500 * 1024 * 1024
// storageUsed is tracked in kb (bytes / 1000) - 5,000,000kb = 5GB
const STORAGE_LIMIT_KB = 5000000
const MAX_CONCURRENT_UPLOADS = 2

const ALLOWED_TYPES = {
  'video/mp4': { type: 'video', extension: 'mp4' },
  'image/png': { type: 'image', extension: 'png' },
  'image/jpeg': { type: 'image', extension: 'jpg' }
}
// Fallback when the browser doesn't report a mime type for the file
const EXTENSION_TYPES = { mp4: 'video/mp4', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' }

// Codes match the server (ForeplayServer services/manualUpload/validation.js)
const ERROR_MESSAGES = {
  INVALID_FILE_TYPE: 'Only .mp4, .png and .jpeg files are supported',
  FILE_TOO_LARGE: 'Files must be 500MB or smaller',
  STORAGE_LIMIT_REACHED: 'Storage limit reached (5GB)',
  UPLOAD_NOT_FOUND: 'Upload not found, please upload the file again',
  UPLOAD_FAILED: 'Upload failed, please try again'
}
const RETRYABLE_ERRORS = ['UPLOAD_NOT_FOUND', 'UPLOAD_FAILED']
const IN_PROGRESS_STATUSES = ['queued', 'uploading', 'processing']
const DRAG_EVENTS = ['dragenter', 'dragover', 'dragleave', 'drop']

export default {
  name: 'ManualUpload',
  components: {
    File,
    UploadIcon
  },
  props: {
    currentBoard: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      uploads: [],
      dragActive: false,
      dropdownOpenedId: null,
      completedCount: 0
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    acceptedTypes () {
      return Object.keys(ALLOWED_TYPES).join(',')
    },
    inProgressCount () {
      return this.uploads.filter(upload => IN_PROGRESS_STATUSES.includes(upload.status)).length
    },
    isBusy () {
      return this.inProgressCount > 0
    }
  },
  watch: {
    isBusy (busy) {
      this.$emit('uploading', busy)
    },
    dropdownOpenedId (val) {
      // We listen to see if one of the board select dropdowns is opened so we can lock the scroll
      const container = this.$refs['uploaded-files-list']
      if (val && container) {
        this.scrollEventListener = (e) => e.preventDefault() // Prevent scrolling
        container.addEventListener('wheel', this.scrollEventListener)
        container.addEventListener('touchmove', this.scrollEventListener)
      } else if (container) {
        // Remove listeners to re-enable scrolling
        container.removeEventListener('wheel', this.scrollEventListener)
        container.removeEventListener('touchmove', this.scrollEventListener)
      }
    }
  },
  created () {
    // Firebase upload tasks are kept out of reactive data
    this.uploadTasks = {}
  },
  mounted () {
    // Stops the browser from opening files dropped outside the upload area
    DRAG_EVENTS.forEach((eventName) => {
      document.body.addEventListener(eventName, this.preventDefaults)
    })
  },
  beforeDestroy () {
    DRAG_EVENTS.forEach((eventName) => {
      document.body.removeEventListener(eventName, this.preventDefaults)
    })

    // Cancelled uploads are never committed to the bucket, queued files are dropped
    this.isDestroyed = true
    Object.values(this.uploadTasks).forEach(task => task.cancel())

    // Clean up scroll lock event listeners
    const scrollContainer = this.$refs['uploaded-files-list']
    if (scrollContainer && this.scrollEventListener) {
      scrollContainer.removeEventListener('wheel', this.scrollEventListener)
      scrollContainer.removeEventListener('touchmove', this.scrollEventListener)
    }
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER']),
    preventDefaults (e) {
      e.preventDefault()
    },
    onDrop (e) {
      this.dragActive = false
      this.addFiles(e.dataTransfer.files)
    },
    onInputChange (e) {
      this.addFiles(e.target.files)
      // Reset so selecting the same file again still fires change
      this.$refs.fileInput.value = ''
    },

    // Queue Methods
    addFiles (fileList) {
      // Added one at a time so the quota check counts earlier files from the same drop
      for (const file of fileList) {
        this.uploads.push(this.createUpload(file))
      }
      this.processQueue()
    },
    createUpload (file) {
      const extension = file.name.split('.').pop().toLowerCase()
      const contentType = ALLOWED_TYPES[file.type] ? file.type : EXTENSION_TYPES[extension]

      const upload = {
        key: nanoid(),
        file,
        name: file.name,
        contentType,
        type: ALLOWED_TYPES[contentType]?.type || null,
        size: file.size,
        status: 'queued',
        progress: 0,
        error: null,
        ad: null
      }

      const errorCode = this.validateUpload(upload)
      if (errorCode) this.setUploadError(upload, errorCode)

      return upload
    },
    validateUpload (upload) {
      if (!upload.contentType) return 'INVALID_FILE_TYPE'
      if (upload.size > MAX_FILE_BYTES) return 'FILE_TOO_LARGE'

      // Early warning only - the server enforces the quota
      const inProgressKb = this.uploads
        .filter(existing => IN_PROGRESS_STATUSES.includes(existing.status))
        .reduce((total, existing) => total + Math.ceil(existing.size / 1000), 0)
      const storageUsed = this.getUser.storageUsed || 0
      if (storageUsed + inProgressKb + Math.ceil(upload.size / 1000) > STORAGE_LIMIT_KB) return 'STORAGE_LIMIT_REACHED'

      return null
    },
    setUploadError (upload, code, message) {
      // Only trust the server message for known codes (eg. auth failures respond without a code)
      const isKnownCode = !!ERROR_MESSAGES[code]
      const errorCode = isKnownCode ? code : 'UPLOAD_FAILED'
      upload.status = 'error'
      upload.error = {
        code: errorCode,
        message: (isKnownCode && message) || ERROR_MESSAGES[errorCode],
        retryable: RETRYABLE_ERRORS.includes(errorCode)
      }
    },
    processQueue () {
      // In flight server requests still resolve after the modal closes
      if (this.isDestroyed) return

      const activeCount = this.uploads.filter(upload => ['uploading', 'processing'].includes(upload.status)).length
      const nextUploads = this.uploads
        .filter(upload => upload.status === 'queued')
        .slice(0, Math.max(MAX_CONCURRENT_UPLOADS - activeCount, 0))

      nextUploads.forEach(upload => this.runUpload(upload))

      if (!this.isBusy && this.completedCount) this.finishBatch()
    },
    retryUpload (upload) {
      upload.status = 'queued'
      upload.progress = 0
      upload.error = null
      this.processQueue()
    },
    finishBatch () {
      this.$showAlert({
        message: this.completedCount === 1 ? 'File uploaded' : `${this.completedCount} files uploaded`,
        type: 'success'
      })
      this.completedCount = 0

      // Emit update for ad refresh
      this.$emit('refresh')
    },

    // Uploading Methods
    async runUpload (upload) {
      upload.status = 'uploading'

      // 1: Upload the file to the user's pending folder
      let filePath
      let storageRef
      try {
        const { uid } = firebase.auth().currentUser
        const { extension } = ALLOWED_TYPES[upload.contentType]
        filePath = `pending/${uid}/${nanoid()}/${nanoid()}.${extension}`
        storageRef = firebase.app().storage(MANUAL_UPLOAD_BUCKET).ref(filePath)

        const task = storageRef.put(upload.file, { contentType: upload.contentType })
        this.uploadTasks[upload.key] = task
        task.on('state_changed', (snapshot) => {
          upload.progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        })
        await task
      } catch (error) {
        // Cancelled when the modal closes mid upload
        if (error.code === 'storage/canceled') return

        console.error('Manual upload failed', error)
        if (storageRef) storageRef.delete().catch(() => {})
        this.setUploadError(upload, 'UPLOAD_FAILED')
        this.processQueue()
        return
      } finally {
        delete this.uploadTasks[upload.key]
      }

      // 2: Have the server validate it, generate a thumbnail and create the ad
      upload.status = 'processing'
      try {
        const { ad, storageUsed } = await FirebaseAPI.Advertisements.createManualUpload({
          filePath,
          originalName: upload.name,
          boardIds: this.currentBoard ? [this.currentBoard] : []
        })

        upload.ad = ad
        upload.status = 'done'
        this.completedCount += 1
        // Parallel uploads can resolve out of order, keep the highest total
        this.SET_USER({ ...this.getUser, storageUsed: Math.max(this.getUser.storageUsed || 0, storageUsed) })
      } catch (error) {
        // The server cleans up its own failures, this covers requests that never reached it
        storageRef.delete().catch(() => {})
        const { code, message } = error.response?.data || {}
        this.setUploadError(upload, code, message)
      }

      this.processQueue()
    }
  }
}
</script>

<style scoped>
.uploaded-files-list {
  max-height: 234px;
}
.uploaded-files-list::-webkit-scrollbar {
  width: 3px;
}
.uploaded-files-list::-webkit-scrollbar-thumb {
  background-color: #C1C7D0;
  border-radius: 18px;
}
</style>
